/**
 * N8N webhook integration.
 *
 * Each tool form submission fires a POST to its corresponding N8N workflow
 * IN ADDITION to the Supabase edge function that drafts the document.
 *
 * URLs can be configured two ways (env wins over the hardcoded default):
 *   1. Vite env vars at build time:
 *        VITE_N8N_WEBHOOK_AFFIDAVIT, VITE_N8N_WEBHOOK_LOD,
 *        VITE_N8N_WEBHOOK_RESOLUTION, VITE_N8N_WEBHOOK_AGREEMENT,
 *        VITE_N8N_WEBHOOK_CP, VITE_N8N_WEBHOOK_COMPLIANCE,
 *        VITE_N8N_WEBHOOK_DD, VITE_N8N_WEBHOOK_PATRICIA
 *   2. The DEFAULT_WEBHOOKS map below (hardcoded fallback).
 *
 * Wiring is fire-and-forget: a webhook failure must NEVER block the user.
 */

import type { ToolKey } from "@/data/types";

export type WebhookKey = ToolKey | "patricia";

/** Hardcoded fallback URLs, used when the matching env var is not set. */
const DEFAULT_WEBHOOKS: Record<WebhookKey, string> = {
  affidavit: "",
  lod: "",
  resolution: "",
  agreement: "",
  cp: "",
  compliance: "",
  dd: "",
  patricia: "",
};

const ENV_KEYS: Record<WebhookKey, string> = {
  affidavit: "VITE_N8N_WEBHOOK_AFFIDAVIT",
  lod: "VITE_N8N_WEBHOOK_LOD",
  resolution: "VITE_N8N_WEBHOOK_RESOLUTION",
  agreement: "VITE_N8N_WEBHOOK_AGREEMENT",
  cp: "VITE_N8N_WEBHOOK_CP",
  compliance: "VITE_N8N_WEBHOOK_COMPLIANCE",
  dd: "VITE_N8N_WEBHOOK_DD",
  patricia: "VITE_N8N_WEBHOOK_PATRICIA",
};

/** Resolve the URL for a given tool, env first, then the hardcoded default. */
export function getWebhookUrl(key: WebhookKey): string {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
  const fromEnv = env[ENV_KEYS[key]];
  if (fromEnv && fromEnv.trim()) return fromEnv.trim();
  return DEFAULT_WEBHOOKS[key] ?? "";
}

/** True if the tool has a configured webhook URL. */
export function isWebhookConfigured(key: WebhookKey): boolean {
  return !!getWebhookUrl(key);
}

export type WebhookPayload = {
  tool: WebhookKey;
  documentId?: string;
  userId?: string;
  matterId?: string | null;
  clientId?: string | null;
  title?: string;
  input: Record<string, unknown>;
  submittedAt: string;
};

/** Wire envelope sent to N8N. Stable shape so workflows can rely on it. */
type WebhookEnvelope = WebhookPayload & {
  source: "casey-tools";
  version: 1;
  /** Where N8N should POST the finished draft back to (if applicable). */
  callbackUrl?: string;
};

const TIMEOUT_MS = 8000;

/**
 * Fire-and-forget POST to the N8N workflow for a given tool.
 *
 * - Returns immediately; never throws.
 * - Aborts after 8s so a hung workflow can never leak resources.
 * - Logs failures to the console only, the user-facing flow continues.
 */
export function fireN8nWebhook(key: WebhookKey, payload: WebhookPayload): void {
  const url = getWebhookUrl(key);
  if (!url) {
    // Not configured yet, silent no-op.
    return;
  }

  const supabaseUrl = (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_SUPABASE_URL;
  const callbackUrl = supabaseUrl
    ? `${supabaseUrl}/functions/v1/n8n-callback`
    : undefined;

  const envelope: WebhookEnvelope = {
    source: "casey-tools",
    version: 1,
    callbackUrl,
    ...payload,
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  // Fire-and-forget. We deliberately do NOT await this.
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(envelope),
    signal: controller.signal,
    // keepalive lets the request survive page navigation after submit.
    keepalive: true,
  })
    .catch((err) => {
      console.warn(`[n8n] webhook "${key}" failed:`, err);
    })
    .finally(() => clearTimeout(timer));
}
